import type { CommissionGroupDto, ProductSelectionDto, ApplicableProductApi } from './types';
import { CommissionProductSelectMode, ProductTypeIdApi, SelectionModeApi } from './types';

const PRODUCT_TYPE_NAME_TO_ID: Record<string, number> = {
  Course: ProductTypeIdApi.Course,
  Accommodation: ProductTypeIdApi.Accommodation,
  AddOn: ProductTypeIdApi.AddOn,
  AddOns: ProductTypeIdApi.AddOn,
  Transfer: ProductTypeIdApi.Transfer,
  Fee: ProductTypeIdApi.Fee,
  Fees: ProductTypeIdApi.Fee,
  StandardFees: ProductTypeIdApi.Fee,
  Package: ProductTypeIdApi.Package,
  Packages: ProductTypeIdApi.Package,
};

const SELECTION_MODE_STRING_TO_NUM: Record<string, number> = {
  NoSelectionMode: SelectionModeApi.NoSelectionMode,
  SpecificProductType: SelectionModeApi.SpecificProductType,
  IncludeSelected: SelectionModeApi.IncludeSelected,
  ExcludeSelected: SelectionModeApi.ExcludeSelected,
  IncludeAll: SelectionModeApi.IncludeAll,
};

function parseProductId(value: number | string): number {
  if (typeof value === 'number') return value;
  const s = String(value).trim();
  const match = s.match(/-(\d+)$/);
  return match ? parseInt(match[1], 10) : parseInt(s, 10) || 0;
}

function mapSelectionMode(api: ApplicableProductApi, fromExcludedList: boolean): number {
  const raw = api.selectionMode;
  if (typeof raw === 'number' && raw >= 0 && raw <= 4) return raw;
  const hasProductIds = (api.productIds?.length ?? 0) > 0;
  const hasAccommodationTypes = (api.accommodationTypeIds?.length ?? 0) > 0;
  const hasAddOnTypes = (api.addOnTypeIds?.length ?? 0) > 0;
  if (fromExcludedList && !hasProductIds && !hasAccommodationTypes && !hasAddOnTypes)
    return SelectionModeApi.NoSelectionMode;
  const mode = (String(raw || '').trim() || '').replace(/\s+/g, '');
  const parsed = SELECTION_MODE_STRING_TO_NUM[mode];
  if (parsed !== undefined) return parsed;
  if (fromExcludedList) return SelectionModeApi.ExcludeSelected;
  if ((mode === 'NoSelectionMode' || mode === '') && (hasProductIds || hasAccommodationTypes || hasAddOnTypes))
    return SelectionModeApi.IncludeSelected;
  return SelectionModeApi.SpecificProductType;
}

function applicableProductToSelection(ap: ApplicableProductApi, fromExcludedList: boolean): ProductSelectionDto | null {
  const productTypeName = (ap.productTypeName || '').trim();
  const productTypeId = PRODUCT_TYPE_NAME_TO_ID[productTypeName] ?? ProductTypeIdApi.Course;
  const selectionMode = mapSelectionMode(ap, fromExcludedList);

  const selection: ProductSelectionDto = {
    productTypeId,
    accommodationTypeIds: ap.accommodationTypeIds ?? [],
    addOnTypeIds: ap.addOnTypeIds ?? [],
    productIds: [],
    selectionMode,
    minDuration: ap.minDuration ?? 0,
    maxDuration: ap.maxDuration ?? 0,
  };

  if (selectionMode === SelectionModeApi.SpecificProductType || selectionMode === SelectionModeApi.IncludeAll || selectionMode === SelectionModeApi.NoSelectionMode) {
    selection.productIds = [];
    selection.accommodationTypeIds = [];
    selection.addOnTypeIds = [];
  } else if ((ap.productIds?.length ?? 0) > 0) {
    selection.productIds = (ap.productIds ?? []).map(parseProductId).filter((id) => id > 0);
    selection.addOnTypeIds = [];
  } else if ((ap.addOnTypeIds?.length ?? 0) > 0 && productTypeId === ProductTypeIdApi.AddOn) {
    selection.addOnTypeIds = (ap.addOnTypeIds ?? []).map(parseProductId).filter((id) => id > 0);
  }

  return selection;
}

export function parseCommissionGroupResponse(api: unknown): CommissionGroupDto[] {
  if (!api || typeof api !== 'object') {
    return [];
  }
  const data = api as Record<string, unknown>;
  const rules = Array.isArray(data.rules) ? data.rules : Array.isArray(data.include) || Array.isArray(data.exclude) ? [data] : null;
  if (rules) {
    return rules
      .map((r: any) => {
        if (!r || typeof r !== 'object') return null;
        const ps: ProductSelectionDto[] = [];
        const feeIds: number[] = [];
        if (Array.isArray(r.feeIds)) {
          r.feeIds.forEach((x: any) => {
            const p = parseProductId(x);
            if (p > 0) feeIds.push(p);
          });
        }
        const md = (m: number, ex: boolean) =>
          m === CommissionProductSelectMode.Any
            ? ex
              ? SelectionModeApi.NoSelectionMode
              : SelectionModeApi.SpecificProductType
            : ex
              ? SelectionModeApi.ExcludeSelected
              : SelectionModeApi.IncludeSelected;
        const push = (pt: number, m: number, ex: boolean, ids: any[], mn: any, mx: any) =>
          ps.push({
            productTypeId: pt,
            accommodationTypeIds: [],
            addOnTypeIds: [],
            productIds:
              m !== CommissionProductSelectMode.Any && Array.isArray(ids)
                ? ids.map(parseProductId).filter((id) => id > 0)
                : [],
            selectionMode: md(m, ex),
            minDuration: mn != null ? Number(mn) || 0 : 0,
            maxDuration: mx != null ? Number(mx) || 0 : 0,
          });
        [...(r.include || []), ...(r.exclude || []).map((x: any) => ({ ...x, _ex: true }))].forEach((it: any) => {
          if (!it) return;
          const ex = !!it._ex;
          const m = Number(it.mode ?? 0);
          if (it.field === 'course') push(ProductTypeIdApi.Course, m, ex, it.courseIds || [], it.minWeeks, it.maxWeeks);
          else if (it.field === 'transfer') push(ProductTypeIdApi.Transfer, m, ex, it.transferIds || [], it.minWeeks, it.maxWeeks);
          else if (it.field === 'fee') {
            ps.push({
              productTypeId: ProductTypeIdApi.Fee,
              accommodationTypeIds: [],
              addOnTypeIds: [],
              productIds:
                m !== CommissionProductSelectMode.Any && Array.isArray(it.feeIds)
                  ? it.feeIds.map(parseProductId).filter((id: number) => id > 0)
                  : [],
              selectionMode: md(m, ex),
              minDuration: 0,
              maxDuration: 0,
            });
          } else if (it.field === 'addOn') {
            const s: ProductSelectionDto = {
              productTypeId: ProductTypeIdApi.AddOn,
              accommodationTypeIds: [],
              addOnTypeIds: [],
              productIds: [],
              selectionMode: md(m, ex),
              minDuration: it.minWeeks != null ? Number(it.minWeeks) || 0 : 0,
              maxDuration: it.maxWeeks != null ? Number(it.maxWeeks) || 0 : 0,
            };
            const addOnKind = it.kind ?? it.Kind;
            if (m === CommissionProductSelectMode.Type && addOnKind != null && addOnKind !== '') {
              const k = parseProductId(addOnKind);
              if (k > 0) s.addOnTypeIds = [k];
            } else if (m === CommissionProductSelectMode.Select && Array.isArray(it.addOnIds)) {
              s.productIds = it.addOnIds.map(parseProductId).filter((id: number) => id > 0);
            }
            ps.push(s);
          } else if (it.field === 'package') {
            push(ProductTypeIdApi.Package, m, ex, it.packageIds || [], null, null);
          } else if (it.field === 'accommodation') {
            const s = { productTypeId: ProductTypeIdApi.Accommodation, accommodationTypeIds: [] as number[], addOnTypeIds: [] as number[], productIds: [] as number[], selectionMode: md(m, ex), minDuration: it.minWeeks != null ? Number(it.minWeeks) || 0 : 0, maxDuration: it.maxWeeks != null ? Number(it.maxWeeks) || 0 : 0 };
            const accKind = it.kind ?? it.Kind;
            if (m === CommissionProductSelectMode.Type && accKind != null && accKind !== '') {
              const k = parseProductId(accKind);
              if (k > 0) s.accommodationTypeIds = [k];
            } else if (m === CommissionProductSelectMode.Select && Array.isArray(it.accommodationIds)) {
              s.productIds = (it.accommodationIds as unknown[]).map(parseProductId).filter((id) => id > 0);
            }
            ps.push(s);
          }
        });
        return { id: Number(r.id ?? 0) || 0, name: String(r.name ?? ''), active: r.active !== false, productSelections: ps, feeIds } as CommissionGroupDto;
      })
      .filter(Boolean) as CommissionGroupDto[];
  }

  const legacyData = data as Record<string, unknown>;
  if (Array.isArray(legacyData.productSelections) && legacyData.productSelections.length > 0) {
    const first = legacyData.productSelections[0] as Record<string, unknown>;
    if (first && typeof (first as any).selectionMode === 'number') {
      const dto: CommissionGroupDto = {
        id: (legacyData.id as number) ?? 0,
        name: (legacyData.name as string) ?? '',
        active: legacyData.active !== false,
        productSelections: (legacyData.productSelections as Record<string, unknown>[]).map(
          (ps): ProductSelectionDto => ({
            productTypeId: Number((ps as any).productTypeId ?? 0),
            accommodationTypeIds: Array.isArray((ps as any).accommodationTypeIds)
              ? ((ps as any).accommodationTypeIds as number[])
              : [],
            addOnTypeIds: Array.isArray((ps as any).addOnTypeIds) ? ((ps as any).addOnTypeIds as number[]) : [],
            productIds: Array.isArray((ps as any).productIds)
              ? ((ps as any).productIds as (number | string)[]).map(parseProductId).filter((id) => id > 0)
              : [],
            selectionMode: Number((ps as any).selectionMode ?? SelectionModeApi.SpecificProductType),
            minDuration: Number((ps as any).minDuration ?? 0),
            maxDuration: Number((ps as any).maxDuration ?? 0),
          })
        ),
        feeIds: Array.isArray((legacyData as any).feeIds) ? ((legacyData as any).feeIds as number[]).map(Number) : [],
      };
      return [dto];
    }
  }

  const mainList = (legacyData as any).applicableProduct ?? [];
  const excludedList = (legacyData as any).applicableProductExcluded ?? (legacyData as any).excludedProducts ?? [];

  if (!Array.isArray(mainList) && !Array.isArray(excludedList)) return [];

  const productSelections: ProductSelectionDto[] = [];
  (mainList as ApplicableProductApi[]).forEach((ap) => {
    const ps = applicableProductToSelection(ap, false);
    if (ps) productSelections.push(ps);
  });
  (excludedList as ApplicableProductApi[]).forEach((ap) => {
    const ps = applicableProductToSelection(ap, true);
    if (ps) productSelections.push(ps);
  });

  const dto: CommissionGroupDto = {
    id: (legacyData as any).id ?? 0,
    name: (legacyData as any).name ?? '',
    active: (legacyData as any).active !== false,
    productSelections,
    feeIds: Array.isArray((legacyData as any).feeIds) ? ((legacyData as any).feeIds as number[]).map(Number) : [],
  };

  if (!dto.name && (!dto.productSelections || dto.productSelections.length === 0) && (!dto.feeIds || dto.feeIds.length === 0)) return [];
  return [dto];
}